import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import { Tag } from "primereact/tag";

import CursorDataTable from "../../../components/CursorDataTable";
import { useCursorPagination } from "../../../hooks/useCursorPagination";
import { useExcludedStations } from "../../../hooks/useExcludedStations";
import { StationVisibility, useStationVisibility } from "../../../hooks/useStationVisibility";
import { CurrentStatusSort, listCurrentStatus } from "../../../services/stationStatus";
import {
  EVSE_STATUSES,
  EntityType,
  LOCATION_STATUSES,
  StatusPeriod,
} from "../../../types/alerting";
import { formatDuration } from "../../../utils/formatDuration";
import { formatInstant } from "../../../utils/instantFormat";
import { statusStyle } from "../../../utils/statusPresentation";
import AlertMarkers from "../StationStatusDetail/AlertMarkers";
import StationVisibilityTag from "../StationStatusDetail/StationVisibilityTag";

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

const ENTITY_TYPES = [
  { label: "EVSEs", value: "EVSE" },
  { label: "Stations", value: "LOCATION" },
];

const VISIBILITIES = [
  { label: "Public stations", value: "PUBLIC" },
  { label: "Private stations", value: "PRIVATE" },
];

const ORDERS: { label: string; value: CurrentStatusSort }[] = [
  { label: "Newest first", value: "newest" },
  { label: "Oldest first", value: "oldest" },
];

const StationStatusListPage = () => {
  const navigate = useNavigate();
  // Opens on the worklist someone actually came here for: whole stations that have gone quiet.
  const [entityType, setEntityType] = useState<EntityType>("LOCATION");
  const [status, setStatus] = useState<string | null>("TIME_OUT");
  const [sort, setSort] = useState<CurrentStatusSort>("newest");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  /** Waits for typing to settle, so a search is one request rather than one per keystroke. */
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // A new fetcher restarts the list from the first batch; the hook drops stale responses.
  const fetchPage = useCallback(
    (cursor: string | null) =>
      listCurrentStatus({ entityType, status, search, sort, limit: PAGE_SIZE, cursor }),
    [entityType, status, search, sort],
  );

  const { rows, state, hasMore, loadingMore, error, loadMore } =
    useCursorPagination<StatusPeriod>(fetchPage);

  const [visibilityFilter, setVisibilityFilter] = useState<StationVisibility | null>(null);
  const visibility = useStationVisibility(rows.map((p) => p.locationId));
  const excluded = useExcludedStations();
  const visibleRows = visibilityFilter
    ? rows.filter((p) => visibility[p.locationId] === visibilityFilter)
    : rows;

  const statuses = entityType === "LOCATION" ? LOCATION_STATUSES : EVSE_STATUSES;
  const statusOptions = statuses.map((value) => ({ label: value, value }));

  /** Switching the entity type can strand a status the new type does not have. */
  const changeEntityType = (next: EntityType) => {
    const available = next === "LOCATION" ? LOCATION_STATUSES : EVSE_STATUSES;
    setEntityType(next);
    if (status && !available.includes(status)) setStatus(null);
  };

  return (
    <div className="surface-section px-4 py-5 md:px-6 lg:px-8">
      <div className="mb-4">
        <div className="text-2xl font-semibold">Current station status</div>
        <p className="text-color-secondary mt-2 mb-0">
          Everything presently holding a status, as a worklist. Owners are only alerted for public
          stations.
        </p>
      </div>

      <div className="flex flex-wrap align-items-end gap-3 mb-4">
        <div className="flex flex-column gap-2">
          <label htmlFor="status-search" className="text-sm">Search</label>
          <IconField iconPosition="left">
            <InputIcon className="pi pi-search" />
            <InputText
              id="status-search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search station or EVSE…"
              maxLength={100}
              className="w-full md:w-20rem"
              data-testid="status-search"
            />
          </IconField>
        </div>
        <div className="flex flex-column gap-2">
          <label htmlFor="entity-type" className="text-sm">Showing</label>
          <Dropdown
            inputId="entity-type"
            value={entityType}
            options={ENTITY_TYPES}
            onChange={(e) => changeEntityType(e.value)}
            data-testid="entity-type-dropdown"
          />
        </div>
        <div className="flex flex-column gap-2">
          <label htmlFor="status" className="text-sm">Status</label>
          <Dropdown
            inputId="status"
            value={status}
            options={statusOptions}
            onChange={(e) => setStatus(e.value ?? null)}
            showClear
            placeholder="Any status"
            data-testid="status-dropdown"
          />
        </div>
        <div className="flex flex-column gap-2">
          <label htmlFor="visibility" className="text-sm">Visibility</label>
          <Dropdown
            inputId="visibility"
            value={visibilityFilter}
            options={VISIBILITIES}
            onChange={(e) => setVisibilityFilter(e.value ?? null)}
            showClear
            placeholder="Public and private"
            data-testid="visibility-dropdown"
          />
        </div>
        <div className="flex flex-column gap-2">
          <label htmlFor="order" className="text-sm">Order</label>
          <Dropdown
            inputId="order"
            value={sort}
            options={ORDERS}
            onChange={(e) => setSort(e.value)}
            data-testid="order-dropdown"
          />
        </div>
      </div>

      <CursorDataTable<StatusPeriod>
        state={state}
        rows={visibleRows}
        dataKey="entityId"
        hasMore={hasMore}
        loadingMore={loadingMore}
        onLoadMore={loadMore}
        error={error}
        emptyMessage={
          search
            ? `Nothing matches "${search}".`
            : status
              ? `Nothing is currently ${status}.`
              : "Nothing is currently recorded in any status."
        }
        refusedMessage="Viewing station status requires an administrator entitlement."
        failedMessage="Could not load the current status"
      >
        <Column
          header={entityType === "LOCATION" ? "Station" : "EVSE"}
          body={(p: StatusPeriod) => (
            <button
              type="button"
              className="p-link text-primary"
              onClick={() =>
                navigate(`/alerting/status/${p.entityType}/${encodeURIComponent(p.entityId)}`)
              }
            >
              {p.entityId}
            </button>
          )}
        />
        <Column field="locationId" header="Station" />
        <Column
          header="Visibility"
          body={(p: StatusPeriod) => (
            <StationVisibilityTag
              visibility={visibility[p.locationId]}
              excluded={excluded.has(p.locationId)}
            />
          )}
        />
        <Column header="Status" body={(p: StatusPeriod) => <Tag value={p.status} style={statusStyle(p.status)} />} />
        <Column header="Since" body={(p: StatusPeriod) => formatInstant(p.startedAt)} />
        <Column header="Duration" body={(p: StatusPeriod) => formatDuration(p.durationSeconds)} />
        <Column header="Alert" body={(p: StatusPeriod) => <AlertMarkers period={p} />} />
      </CursorDataTable>
    </div>
  );
};

export default StationStatusListPage;
